import React, { lazy, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  Typography,
  Box,
  ImageList,
  ImageListItem,
  IconButton,
  ImageListItemBar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { COLOR, ENG, FR } from 'config/constants';
import { useAuth, useListing, useLanguage, useListingVideoTemplates } from 'hooks';
import RadioButtonUncheckedOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { VideoTemplate } from 'models';
import { LensRounded } from '@material-ui/icons';
import { isMobile } from 'utils/screen';

const ReactPlayer = lazy(() => {
  return import('react-player');
});

const useStyles = makeStyles({
  title: {
    fontWeight: 'bold',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
  },
  templateImage: {
    width: '100%',
  },
  icon: {
    color: COLOR.LIGHT_BLUE,
    flexDirection: 'column',
    display: 'flex',
  },
  greyIcon: {
    color: COLOR.GREY,
    flexDirection: 'column',
    display: 'flex',
  },
  videoPlayer: {
    marginBottom: 10,
    position: 'relative',
    width: '100% !important',
    height: 'auto !important',

    '& video': {
      maxHeight: 300,
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginBottom: 5,
  },
  pictureCountText: {
    color: COLOR.WHITE,
  },
  warningText: {
    color: COLOR.RED,
    backgroundColor: COLOR.LIGHT_RED_CONTENT,
    paddingLeft: 5,
    paddingRight: 5,
    borderRadius: 8,
  },
});
interface Props {
  selectedTemplate: VideoTemplate;
  onSelectTemplate: (_: VideoTemplate) => void;
}
function Templates({ selectedTemplate, onSelectTemplate }: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const language = useLanguage();
  const { token } = useAuth();
  const path = window.location.pathname;
  const arr = path.split('/');
  const listing_token = arr[arr.length - 1];
  const { listing } = useListing(listing_token);
  const theme_lang = language === ENG ? 'en' : FR;
  const { videoTemplates } = useListingVideoTemplates(token, listing.id);
  const listing_video_picture_count = listing.pictures.filter((picture) => {
    return picture.included_in_video === true;
  }).length;

  const fmtPhotos = intl.formatMessage({ id: 'deliver.video_slideshow.photos' });

  return (
    <Box>
      {selectedTemplate && (
        <ReactPlayer
          className={styles.videoPlayer}
          url={selectedTemplate.preview_url}
          controls
          width='100%'
          height='auto'
          volume={0.5}
        />
      )}
      <Box>
        <ImageList gap={12} cols={isMobile() ? 1 : 2}>
          {videoTemplates.length &&
            videoTemplates.map((template: VideoTemplate, index) => {
              const isSelected = selectedTemplate?.id === template.id;
              return (
                <ImageListItem key={index}>
                  <img
                    className={styles.templateImage}
                    src={template.thumbnail_url}
                    alt='thumbnail'
                  />
                  <ImageListItemBar
                    title={template.template_name}
                    subtitle={`${template[`theme_${theme_lang}`]}`}
                    actionIcon={
                      listing_video_picture_count < template.listing_picture_count ? (
                        <Box className={styles.column}>
                          <IconButton>
                            <LensRounded className={styles.greyIcon} />
                          </IconButton>
                          <Typography className={styles.warningText}>
                            {`${template.listing_picture_count} ${fmtPhotos}`}
                          </Typography>
                        </Box>
                      ) : (
                        <Box className={styles.column}>
                          <IconButton
                            onClick={() => {
                              return onSelectTemplate(template);
                            }}
                            className={styles.icon}
                          >
                            {isSelected ? (
                              <CheckCircleIcon />
                            ) : (
                              <RadioButtonUncheckedOutlinedIcon />
                            )}
                          </IconButton>
                          <Typography className={styles.pictureCountText}>
                            {`${template.listing_picture_count} ${fmtPhotos}`}
                          </Typography>
                        </Box>
                      )
                    }
                  />
                </ImageListItem>
              );
            })}
        </ImageList>
      </Box>
    </Box>
  );
}
export default Templates;
